"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { getSupabaseBrowserClient } from "@/lib/supabase";

type Category = { id: string; name: string; slug: string; description: string | null };
type Topic = {
  id: string;
  title: string;
  body: string;
  created_at: string;
  category_id: string | null;
  moderation_status: string;
  is_pinned: boolean;
  is_locked: boolean;
  author_id: string | null;
  archived_at: string | null;
};
type AdminRow = { role: string };
type TopicLike = { topicId: string; count: number; liked: boolean };

type CommunityForumProps = {
  categorySlug?: string;
  searchQuery?: string;
  searchMode?: boolean;
  ownedOnly?: boolean;
};

export function CommunityForum({ categorySlug, searchQuery = "", searchMode = false, ownedOnly = false }: CommunityForumProps) {
  const supabase = getSupabaseBrowserClient();
  const [categories, setCategories] = useState<Category[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicLikes, setTopicLikes] = useState<Record<string, TopicLike>>({});
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [topicFiles, setTopicFiles] = useState<FileList | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterCategory, setFilterCategory] = useState("all");
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredTopics = useMemo(() => topics.filter((topic) => {
    const matchesSearch = !normalizedSearchQuery || (topic.title + " " + topic.body).toLowerCase().includes(normalizedSearchQuery);
    const matchesCategory = filterCategory === "all" || topic.category_id === filterCategory;
    return matchesSearch && matchesCategory;
  }), [filterCategory, normalizedSearchQuery, topics]);
  const selectedCategory = useMemo(() => categories.find((category) => category.id === filterCategory), [categories, filterCategory]);

  useEffect(() => {
    async function load() {
      let admin = false;
      try {
        const { data } = await supabase.auth.getUser();
        if (data.user) {
          const { data: adminRow } = await supabase.from("admin_users").select("role").eq("user_id", data.user.id).maybeSingle<AdminRow>();
          setCurrentUserId(data.user.id);
          admin = Boolean(adminRow);
        }
        if (!data.user) setCurrentUserId(null);
      } catch {
        setCurrentUserId(null);
        admin = false;
      }
      setIsAdmin(admin);

      const categoryQuery = supabase.from("forum_categories").select("id,name,slug,description").order("sort_order");
      const safeSearchTerm = searchQuery.trim().replace(/[%,]/g, " ").replace(/\s+/g, " ").trim();
      let topicQuery = supabase.from("forum_topics").select("id,title,body,created_at,category_id,moderation_status,is_pinned,is_locked,author_id,archived_at").order("is_pinned", { ascending: false }).order("created_at", { ascending: false }).limit(50);
      if (safeSearchTerm) topicQuery = topicQuery.or(`title.ilike.%${safeSearchTerm}%,body.ilike.%${safeSearchTerm}%`);
      let countQuery = supabase.from("forum_topics").select("category_id").is("archived_at", null);
      if (ownedOnly) {
        const { data } = await supabase.auth.getUser();
        if (!data.user) {
          setCategories([]);
          setTopics([]);
          setTopicLikes({});
          setMessage("Log in to view your forum posts.");
          return;
        }
        setCurrentUserId(data.user.id);
        topicQuery = topicQuery.eq("author_id", data.user.id);
      } else {
        topicQuery = topicQuery.is("archived_at", null);
      }
      if (!admin && !ownedOnly) {
        topicQuery = topicQuery.eq("moderation_status", "visible");
        countQuery = countQuery.eq("moderation_status", "visible");
      }

      const [cats, tops, counts] = await Promise.all([categoryQuery, topicQuery, countQuery]);
      const nextCategories = (cats.data as Category[] | null) ?? [];
      const nextCounts = ((counts.data as Pick<Topic, "category_id">[] | null) ?? []).reduce<Record<string, number>>((total, topic) => {
        if (topic.category_id) total[topic.category_id] = (total[topic.category_id] ?? 0) + 1;
        return total;
      }, {});
      const matchedCategory = categorySlug ? nextCategories.find((category) => category.slug === categorySlug) : null;

      const nextTopics = (tops.data as Topic[] | null) ?? [];
      setCategories(nextCategories);
      setTopics(nextTopics);
      setCategoryCounts(nextCounts);
      setFilterCategory(matchedCategory ? matchedCategory.id : "all");
      if (nextCategories[0]) setCategoryId((current) => current || matchedCategory?.id || nextCategories[0].id);
      if (nextTopics.length) {
        const topicIds = nextTopics.map((topic) => topic.id);
        const { data: sessionData } = await supabase.auth.getSession();
        const headers = sessionData.session?.access_token ? { Authorization: "Bearer " + sessionData.session.access_token } : undefined;
        const likesResponse = await fetch("/api/forum/topic-likes?topicIds=" + encodeURIComponent(topicIds.join(",")), { headers });
        const likesPayload = await likesResponse.json() as { likes?: TopicLike[] };
        const likes: Record<string, TopicLike> = {};
        (likesPayload.likes ?? []).forEach((like) => { likes[like.topicId] = like; });
        setTopicLikes(likes);
      } else {
        setTopicLikes({});
      }
      if (tops.error) setMessage(tops.error.message);
      if (categorySlug && !matchedCategory) setMessage("Category not found.");
    }
    void load();
  }, [categorySlug, ownedOnly, searchQuery, supabase, refreshKey]);

  async function createTopic(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) { setMessage("Log in to create a topic."); return; }
    const response = await fetch("/api/forum/topics", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ categoryId, title, body }) });
    const payload = await response.json() as { error?: string; topic?: { id: string } };
    if (!response.ok || !payload.topic?.id) { setMessage(payload.error ?? "Could not create topic."); return; }
    if (topicFiles?.length) {
      const form = new FormData();
      form.append("topicId", payload.topic.id);
      Array.from(topicFiles).forEach((file) => form.append("files", file));
      const upload = await fetch("/api/forum/attachments", { method: "POST", headers: { Authorization: "Bearer " + token }, body: form });
      const uploadPayload = await upload.json() as { error?: string };
      if (!upload.ok) { setMessage(uploadPayload.error ?? "Topic created, but attachment upload failed."); return; }
    }
    setTitle("");
    setBody("");
    setTopicFiles(null);
    event.currentTarget.reset();
    setRefreshKey((current) => current + 1);
  }

  async function toggleTopicLike(topicId: string) {
    setMessage(null);
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) { setMessage("Log in to like topics."); return; }
    const current = topicLikes[topicId] ?? { topicId, count: 0, liked: false };
    setTopicLikes((likes) => ({ ...likes, [topicId]: { topicId, count: Math.max(0, current.count + (current.liked ? -1 : 1)), liked: !current.liked } }));
    const response = await fetch("/api/forum/topic-likes", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ topicId }) });
    const payload = await response.json() as { error?: string };
    if (!response.ok) {
      setTopicLikes((likes) => ({ ...likes, [topicId]: current }));
      setMessage(payload.error ?? "Could not update topic like.");
    }
  }

  async function archiveTopic(topic: Topic) {
    setBusyId(topic.id);
    setMessage(null);
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) { setMessage("Login session expired."); setBusyId(null); return; }
    const response = await fetch("/api/forum/topics/archive", { method: "PATCH", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ topicId: topic.id, archive: !topic.archived_at }) });
    const payload = await response.json() as { error?: string };
    if (!response.ok) setMessage(payload.error ?? "Topic archive update failed.");
    setBusyId(null);
    setRefreshKey((current) => current + 1);
  }

  async function moderateTopic(topic: Topic, changes: Partial<Pick<Topic, "moderation_status" | "is_pinned" | "is_locked">>) {
    setBusyId(topic.id);
    setMessage(null);
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) { setMessage("Login session expired."); setBusyId(null); return; }
    const response = await fetch("/api/admin/forum", { method: "PATCH", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ target: "topic", id: topic.id, moderationStatus: changes.moderation_status, isPinned: changes.is_pinned, isLocked: changes.is_locked }) });
    if (!response.ok) setMessage("Forum moderation update failed.");
    setBusyId(null);
    setRefreshKey((current) => current + 1);
  }

  async function deleteTopic(topic: Topic) {
    if (!window.confirm("Delete this topic and its replies?")) return;
    setBusyId(topic.id);
    setMessage(null);
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) { setMessage("Login session expired."); setBusyId(null); return; }
    const response = await fetch("/api/admin/forum", { method: "DELETE", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ target: "topic", id: topic.id }) });
    if (!response.ok) setMessage("Topic delete failed.");
    setBusyId(null);
    setRefreshKey((current) => current + 1);
  }

  return <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_420px]">
    <div className="grid gap-4">
      {!categorySlug && !searchMode && !ownedOnly ? <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="text-2xl font-semibold text-[#111827]">Categories</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {categories.map((category) => {
            const topicCount = categoryCounts[category.id] ?? 0;
            return <Link key={category.id} href={"/community/categories/" + category.slug} className="rounded-md border border-slate-200 bg-[#F8FAFC] p-4 transition hover:border-[#F68A00] hover:bg-white">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-semibold text-[#111827]">{category.name}</h3>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#64748B]">{topicCount} {topicCount === 1 ? "topic" : "topics"}</span>
              </div>
              <p className="mt-2 text-sm text-[#64748B]">{category.description}</p>
            </Link>;
          })}
        </div>
      </section> : null}

      <section id="topics" className="scroll-mt-24 rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold text-[#111827]">{ownedOnly ? "My posts" : searchMode ? "Search results" : categorySlug ? "Topics" : selectedCategory ? selectedCategory.name + " topics" : "Recent topics"}</h2>
          <div className="flex flex-wrap gap-2">
            {!categorySlug && !searchMode && !ownedOnly ? <select value={filterCategory} onChange={(event) => setFilterCategory(event.target.value)} className="rounded-md border border-[#CBD5E1] px-3 py-2 text-sm outline-none focus:border-[#F68A00]">
              <option value="all">All categories</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select> : <Link href="/community" className="rounded-md border border-[#CBD5E1] px-3 py-2 text-sm font-semibold text-[#111827]">All topics</Link>}
          </div>
        </div>
        {message ? <p className="mt-4 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-[#7C2D12]">{message}</p> : null}
        <div className="mt-5 grid gap-3">
          {searchMode && !normalizedSearchQuery ? <p className="text-sm text-[#64748B]">Enter a search term above.</p> : filteredTopics.length ? filteredTopics.map((topic) => <article key={topic.id} className="rounded-md border border-slate-200 bg-[#F8FAFC] p-4">
            <div className="flex items-start justify-between gap-4">
              <Link href={"/community/topics/" + topic.id} className="min-w-0 flex-1 hover:text-[#F68A00]">
                <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">{topic.is_pinned ? <span>Pinned</span> : null}{topic.is_locked ? <span>Locked</span> : null}{topic.archived_at ? <span>Archived</span> : null}{isAdmin ? <span>{topic.moderation_status}</span> : null}</div>
                <h3 className="mt-2 font-semibold text-[#111827]">{topic.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-[#64748B]">{topic.body}</p>
              </Link>
              <LikeButton count={(topicLikes[topic.id] ?? { topicId: topic.id, count: 0, liked: false }).count} liked={(topicLikes[topic.id] ?? { topicId: topic.id, count: 0, liked: false }).liked} onClick={() => toggleTopicLike(topic.id)} label={(topicLikes[topic.id] ?? { topicId: topic.id, count: 0, liked: false }).liked ? "Unlike this topic" : "Like this topic"} />
            </div>
            {(isAdmin || topic.author_id === currentUserId) ? <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={busyId === topic.id} onClick={() => archiveTopic(topic)} className="rounded-md border border-[#CBD5E1] bg-white px-3 py-1 text-xs font-semibold text-[#111827]">{topic.archived_at ? "Unarchive" : "Archive"}</button>
            </div> : null}
            {isAdmin ? <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={busyId === topic.id} onClick={() => moderateTopic(topic, { is_pinned: !topic.is_pinned })} className="rounded-md border border-[#CBD5E1] bg-white px-3 py-1 text-xs font-semibold text-[#111827]">{topic.is_pinned ? "Unpin" : "Pin"}</button>
              <button type="button" disabled={busyId === topic.id} onClick={() => moderateTopic(topic, { is_locked: !topic.is_locked })} className="rounded-md border border-[#CBD5E1] bg-white px-3 py-1 text-xs font-semibold text-[#111827]">{topic.is_locked ? "Unlock" : "Lock"}</button>
              <button type="button" disabled={busyId === topic.id} onClick={() => moderateTopic(topic, { moderation_status: topic.moderation_status === "visible" ? "hidden" : "visible" })} className="rounded-md border border-[#CBD5E1] bg-white px-3 py-1 text-xs font-semibold text-[#111827]">{topic.moderation_status === "visible" ? "Hide" : "Show"}</button>
              <button type="button" disabled={busyId === topic.id} onClick={() => deleteTopic(topic)} className="rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-semibold text-red-700">Delete</button>
            </div> : null}
          </article>) : <p className="text-sm text-[#64748B]">No matching topics.</p>}
        </div>
      </section>
    </div>

    <form onSubmit={createTopic} className="rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="text-2xl font-semibold text-[#111827]">Create topic</h2>
      <div className="mt-5 grid gap-4">
        <label className="grid gap-2 text-sm font-medium text-[#334155]">Category<select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} className="rounded-md border border-[#CBD5E1] px-3 py-2">{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
        <label className="grid gap-2 text-sm font-medium text-[#334155]">Title<input value={title} onChange={(event) => setTitle(event.target.value)} required className="rounded-md border border-[#CBD5E1] px-3 py-2" /></label>
        <label className="grid gap-2 text-sm font-medium text-[#334155]">Body<textarea value={body} onChange={(event) => setBody(event.target.value)} required rows={6} className="rounded-md border border-[#CBD5E1] px-3 py-2" /></label>
        <label className="grid gap-2 text-sm font-medium text-[#334155]">Attachments<input type="file" multiple accept="image/jpeg,image/png,image/gif,application/pdf,text/plain,text/csv,.jpg,.jpeg,.png,.gif,.pdf,.txt,.csv" onChange={(event) => setTopicFiles(event.target.files)} className="rounded-md border border-[#CBD5E1] px-3 py-2 text-sm" /><span className="text-xs text-[#64748B]">JPG, PNG, GIF, PDF, TXT, or CSV. Up to 5 files, 5 MB each.</span></label>
        <button className="rounded-md bg-[#F68A00] px-5 py-3 text-sm font-semibold text-white">Post topic</button>
      </div>
    </form>
  </div>;
}


function LikeButton({ count, liked, onClick, label }: { count: number; liked: boolean; onClick: () => void; label: string }) {
  return <button type="button" onClick={onClick} className={"inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition " + (liked ? "border-[#F68A00] bg-orange-50 text-[#7C2D12]" : "border-[#CBD5E1] bg-white text-[#334155] hover:border-[#F68A00]")} aria-pressed={liked} aria-label={label}>
    <HeartIcon filled={liked} />
    <span>{count}</span>
  </button>;
}

function HeartIcon({ filled }: { filled: boolean }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6c-1.8-1.8-4.7-1.8-6.5 0L12 6.9 9.7 4.6c-1.8-1.8-4.7-1.8-6.5 0s-1.8 4.7 0 6.5L12 19.9l8.8-8.8c1.8-1.8 1.8-4.7 0-6.5Z" />
  </svg>;
}
